import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import type { AnnouncementFormData, LayoutFamily, CustomTemplateSettings } from '@/types/announcement';
import { DecorationOverlays } from './DecorationOverlays';
import { getBackgroundStyleCSS } from './backgroundTextures';

interface CustomLayoutRendererProps {
  formData: AnnouncementFormData;
  allPhotoUrls?: string[];
  className?: string;
}

// Font class mappings
function getHeadlineFontClass(font: string) {
  switch (font) {
    case 'elegant_serif': return 'font-serif';
    case 'modern_sans': return 'font-sans';
    case 'handwritten_script': return 'italic font-serif';
    case 'bold_editorial': return 'font-sans font-black uppercase';
    default: return 'font-serif';
  }
}

function getBodyFontClass(font: string) {
  return font === 'clean_sans' ? 'font-sans' : 'font-serif';
}

function getPhotoShapeClass(shape: string) {
  switch (shape) {
    case 'rectangle': return 'rounded-none';
    case 'rounded': return 'rounded-xl';
    case 'circle': return 'rounded-full';
    case 'polaroid': return 'rounded-sm';
    default: return 'rounded-xl';
  }
}

function getSmartFontSize(messageLength: number) {
  if (messageLength > 800) return { heading: 'text-xl', body: 'text-sm' };
  if (messageLength > 500) return { heading: 'text-2xl', body: 'text-base' };
  if (messageLength > 200) return { heading: 'text-2xl md:text-3xl', body: 'text-base md:text-lg' };
  return { heading: 'text-3xl md:text-4xl', body: 'text-lg md:text-xl' };
}

// Shared photo renderer
function PhotoBlock({ urls, shape, layout, className }: {
  urls: string[]; shape: string; layout: string; className?: string;
}) {
  if (!urls.length) return null;
  const shapeClass = getPhotoShapeClass(shape);

  switch (layout) {
    case 'hero':
      return (
        <div className={cn('w-full aspect-[4/3] overflow-hidden', shapeClass, className)}>
          <img src={urls[0]} alt="" className="w-full h-full object-cover" />
        </div>
      );
    case 'side_by_side_circles':
      return (
        <div className={cn('flex justify-center gap-4', className)}>
          {urls.slice(0, 2).map((u, i) => (
            <div key={i} className="w-24 h-24 md:w-32 md:h-32 rounded-full overflow-hidden shadow-lg border-2 border-white/60">
              <img src={u} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      );
    case 'stacked_cards':
      return (
        <div className={cn('space-y-3', className)}>
          {urls.slice(0, 3).map((u, i) => (
            <div key={i} className={cn('w-full aspect-video overflow-hidden shadow-md', shapeClass)}>
              <img src={u} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      );
    case 'collage_grid':
      return (
        <div className={cn('grid grid-cols-2 gap-2', className)}>
          {urls.slice(0, 4).map((u, i) => (
            <div key={i} className={cn('aspect-square overflow-hidden shadow-md', shapeClass)}>
              <img src={u} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      );
    case 'tilted_scrapbook':
      return (
        <div className={cn('flex justify-center gap-3', className)}>
          {urls.slice(0, 3).map((u, i) => (
            <div key={i} className="bg-white p-1.5 pb-6 shadow-xl" style={{ transform: `rotate(${(i - 1) * 5}deg)` }}>
              <img src={u} alt="" className="w-24 h-24 md:w-28 md:h-28 object-cover" />
            </div>
          ))}
        </div>
      );
    case 'editorial_framed':
      return (
        <div className={cn('border-2 border-current/10 p-2', className)}>
          <div className={cn('w-full aspect-[3/2] overflow-hidden', shapeClass)}>
            <img src={urls[0]} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      );
    case 'overlapping_layers':
      return (
        <div className={cn('relative h-40 md:h-52', className)}>
          {urls.slice(0, 3).map((u, i) => (
            <div key={i}
              className={cn('absolute w-28 h-28 md:w-36 md:h-36 overflow-hidden shadow-xl border-2 border-white/70', shapeClass)}
              style={{ left: `${i * 20}%`, top: `${i * 8}px`, zIndex: 3 - i, transform: `rotate(${(i - 1) * 3}deg)` }}>
              <img src={u} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      );
    case 'top_banner':
      return (
        <div className={cn('w-full h-32 md:h-48 overflow-hidden', shapeClass, className)}>
          <img src={urls[0]} alt="" className="w-full h-full object-cover" />
        </div>
      );
    case 'floating_cluster':
      return (
        <div className={cn('flex flex-wrap justify-center gap-2', className)}>
          {urls.slice(0, 3).map((u, i) => (
            <div key={i} className={cn('w-20 h-20 md:w-24 md:h-24 overflow-hidden shadow-lg', shapeClass)}
              style={{ transform: `translateY(${(i % 2) * 12}px)` }}>
              <img src={u} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      );
    default: // circular (existing default)
      return (
        <div className={cn('flex justify-center gap-3', className)}>
          {urls.slice(0, 3).map((u, i) => (
            <div key={i} className="w-20 h-20 md:w-24 md:h-24 rounded-full overflow-hidden shadow-lg border-2 border-white/60">
              <img src={u} alt="" className="w-full h-full object-cover" />
            </div>
          ))}
        </div>
      );
  }
}

// ======= 10 LAYOUT COMPONENTS =======

function MagazineCoverLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const hFont = getHeadlineFontClass(cs.headlineFont);
  return (
    <div className="relative min-h-[500px] flex flex-col">
      {photos.length > 0 && (
        <div className="w-full aspect-[4/3] overflow-hidden">
          <img src={photos[0]} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        </div>
      )}
      <div className={cn('relative z-10 p-6 md:p-8', photos.length > 0 ? '-mt-32' : 'pt-12')}>
        {cs.sections.subtitle && <p className="text-xs uppercase tracking-[0.3em] opacity-60 mb-2">A message for you</p>}
        {cs.sections.recipientName && (
          <h2 className={cn(hFont, sizes.heading, cs.titleBold && 'font-bold', 'mb-3 drop-shadow-lg')}>
            {formData.recipientName || 'Someone Special'}
          </h2>
        )}
        {cs.sections.body && (
          <p className={cn(getBodyFontClass(cs.bodyFont), sizes.body, 'leading-relaxed whitespace-pre-wrap mt-4 max-w-md')}>
            {formData.message || ''}
          </p>
        )}
        {cs.sections.signOff && (
          <p className="mt-6 text-sm opacity-60 italic">— With love</p>
        )}
      </div>
    </div>
  );
}

function EditorialStoryLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const hFont = getHeadlineFontClass(cs.headlineFont);
  const bFont = getBodyFontClass(cs.bodyFont);
  const paragraphs = (formData.message || '').split('\n\n').filter(Boolean);
  const pullQuote = paragraphs.length > 1 ? paragraphs[0] : null;
  const bodyParas = pullQuote ? paragraphs.slice(1) : paragraphs;

  return (
    <div className="p-6 md:p-8 space-y-6">
      {cs.sections.subtitle && (
        <p className="text-xs uppercase tracking-[0.3em] opacity-50 text-center">A personal story</p>
      )}
      {cs.sections.title && (
        <h2 className={cn(hFont, sizes.heading, cs.titleBold && 'font-bold', 'text-center')}>
          For {formData.recipientName || 'Someone Special'}
        </h2>
      )}
      <div className="w-20 h-px bg-current opacity-20 mx-auto" />
      {cs.sections.pullQuote && pullQuote && (
        <blockquote className={cn('border-l-2 border-current/20 pl-4 italic opacity-80', bFont, 'text-base md:text-lg')}>
          "{pullQuote}"
        </blockquote>
      )}
      {photos.length > 0 && (
        <PhotoBlock urls={photos} shape={cs.photoShape} layout={cs.photoLayout} className="my-4" />
      )}
      {cs.sections.body && (
        <div className={cn(bFont, sizes.body, 'leading-relaxed space-y-4', cs.textAlignment === 'left' ? 'text-left' : cs.textAlignment === 'mixed' ? 'text-left' : 'text-center')}>
          {bodyParas.map((p, i) => <p key={i} className="whitespace-pre-wrap">{p}</p>)}
        </div>
      )}
      {cs.sections.signOff && (
        <div className="text-center mt-6">
          <div className="w-16 h-px bg-current opacity-20 mx-auto mb-3" />
          <p className="text-sm italic opacity-60">With heartfelt sincerity</p>
        </div>
      )}
    </div>
  );
}

function LoveLetterLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const hFont = getHeadlineFontClass('handwritten_script');
  const bFont = getBodyFontClass(cs.bodyFont);
  return (
    <div className="p-8 md:p-12 text-center space-y-6">
      {cs.sections.recipientName && (
        <p className={cn(hFont, 'text-lg opacity-70')}>My dearest {formData.recipientName || 'you'},</p>
      )}
      {photos.length > 0 && (
        <div className="flex justify-center">
          <div className="w-32 h-32 md:w-40 md:h-40 rounded-full overflow-hidden shadow-2xl border-4 border-white/40">
            <img src={photos[0]} alt="" className="w-full h-full object-cover" />
          </div>
        </div>
      )}
      {cs.sections.body && (
        <p className={cn(hFont, sizes.body, 'leading-loose whitespace-pre-wrap max-w-md mx-auto')}>
          {formData.message || ''}
        </p>
      )}
      {cs.sections.signOff && (
        <div className="pt-4 space-y-1">
          <p className={cn(hFont, 'text-base opacity-50')}>Forever yours,</p>
          <span className="text-xl">♥</span>
        </div>
      )}
    </div>
  );
}

function ScrapbookLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const bFont = getBodyFontClass(cs.bodyFont);
  return (
    <div className="p-5 md:p-8 space-y-5">
      {cs.sections.title && (
        <div className="bg-white/60 dark:bg-black/20 p-3 shadow-md inline-block -rotate-1">
          <h2 className={cn('font-sans font-bold', sizes.heading)}>{formData.recipientName || 'Memories'}</h2>
        </div>
      )}
      {photos.length > 0 && (
        <PhotoBlock urls={photos} shape="polaroid" layout="tilted_scrapbook" className="my-4" />
      )}
      {cs.sections.body && (
        <div className="bg-amber-50/80 dark:bg-amber-950/30 p-4 shadow-sm rotate-[0.5deg] rounded-sm">
          <p className={cn('italic font-serif', sizes.body, 'leading-relaxed whitespace-pre-wrap')}>
            {formData.message || ''}
          </p>
        </div>
      )}
      {cs.sections.stickerRow && (
        <div className="flex gap-2 justify-center text-2xl">
          {'💖✨🌸📸🎀'.split('').filter((_, i) => i % 2 === 0).map((e, i) => (
            <span key={i} className="opacity-40">{e}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function MinimalModernLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const hFont = getHeadlineFontClass('modern_sans');
  const bFont = getBodyFontClass('clean_sans');
  return (
    <div className="p-8 md:p-12 space-y-8">
      {cs.sections.recipientName && (
        <h2 className={cn(hFont, 'text-3xl md:text-5xl font-light tracking-tight')}>
          {formData.recipientName || 'Hello'}
        </h2>
      )}
      <div className="w-12 h-0.5 bg-current opacity-30" />
      {photos.length > 0 && (
        <div className="w-full aspect-video overflow-hidden rounded-lg">
          <img src={photos[0]} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      {cs.sections.body && (
        <p className={cn(bFont, 'text-base md:text-lg leading-relaxed whitespace-pre-wrap text-left opacity-80')}>
          {formData.message || ''}
        </p>
      )}
      {cs.sections.signOff && (
        <p className="text-xs uppercase tracking-widest opacity-40">With love</p>
      )}
    </div>
  );
}

function PolaroidCollageLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const bFont = getBodyFontClass(cs.bodyFont);
  return (
    <div className="p-5 md:p-8 space-y-5">
      {cs.sections.title && (
        <h2 className={cn('font-serif text-2xl md:text-3xl text-center', cs.titleBold && 'font-bold')}>
          {formData.recipientName || 'Our Moments'}
        </h2>
      )}
      {photos.length > 0 && (
        <div className="flex flex-wrap justify-center gap-3">
          {photos.slice(0, 3).map((u, i) => (
            <div key={i} className="bg-white p-1.5 pb-8 shadow-xl" style={{ transform: `rotate(${(i - 1) * 6}deg)` }}>
              <img src={u} alt="" className="w-24 h-24 md:w-32 md:h-32 object-cover" />
              {cs.sections.photoCaption && (
                <p className="text-[9px] text-center text-gray-400 mt-1 font-serif italic">a memory</p>
              )}
            </div>
          ))}
        </div>
      )}
      {cs.sections.body && (
        <p className={cn(bFont, sizes.body, 'leading-relaxed whitespace-pre-wrap text-center max-w-md mx-auto')}>
          {formData.message || ''}
        </p>
      )}
    </div>
  );
}

function BoldPosterLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  return (
    <div className="p-6 md:p-10 text-center space-y-6 min-h-[400px] flex flex-col justify-center">
      {cs.sections.title && (
        <h2 className="font-sans font-black text-4xl md:text-6xl uppercase tracking-tight leading-none">
          {formData.recipientName || 'YOU'}
        </h2>
      )}
      {cs.sections.subtitle && (
        <p className="text-sm uppercase tracking-[0.4em] opacity-50">are amazing</p>
      )}
      {photos.length > 0 && (
        <div className="w-32 h-32 mx-auto rounded-full overflow-hidden shadow-2xl border-4 border-current/10">
          <img src={photos[0]} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      {cs.sections.body && (
        <p className={cn('font-sans text-base md:text-lg leading-relaxed whitespace-pre-wrap max-w-md mx-auto opacity-80')}>
          {formData.message || ''}
        </p>
      )}
      {cs.sections.stickerRow && (
        <div className="flex justify-center gap-3 text-3xl">
          {'🎉🎊✨'.split('').map((e, i) => <span key={i}>{e}</span>)}
        </div>
      )}
    </div>
  );
}

function SoftRomanticLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const hFont = getHeadlineFontClass('elegant_serif');
  return (
    <div className="p-8 md:p-12 text-center space-y-6">
      {cs.sections.recipientName && (
        <h2 className={cn(hFont, 'text-2xl md:text-3xl italic')}>
          For {formData.recipientName || 'my love'}
        </h2>
      )}
      <div className="flex items-center justify-center gap-3">
        <div className="w-10 h-px bg-current opacity-20" />
        <span className="text-rose-400 opacity-50">♥</span>
        <div className="w-10 h-px bg-current opacity-20" />
      </div>
      {photos.length > 0 && (
        <PhotoBlock urls={photos} shape={cs.photoShape} layout={cs.photoLayout} className="my-4" />
      )}
      {cs.sections.body && (
        <p className={cn('font-serif italic', sizes.body, 'leading-loose whitespace-pre-wrap max-w-md mx-auto')}>
          {formData.message || ''}
        </p>
      )}
      {cs.sections.signOff && (
        <p className="text-sm font-serif italic opacity-50 mt-6">With all my heart</p>
      )}
    </div>
  );
}

function MemoryBoardLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const bFont = getBodyFontClass(cs.bodyFont);
  return (
    <div className="p-4 md:p-6 space-y-4">
      {cs.sections.title && (
        <h2 className="font-sans font-bold text-xl md:text-2xl text-center">
          Memories of {formData.recipientName || 'Us'}
        </h2>
      )}
      {photos.length > 0 && (
        <PhotoBlock urls={photos} shape={cs.photoShape} layout="collage_grid" className="my-3" />
      )}
      {cs.sections.body && (
        <div className="bg-amber-50/70 dark:bg-amber-950/20 p-4 rounded-lg shadow-sm">
          <p className={cn(bFont, 'text-sm md:text-base leading-relaxed whitespace-pre-wrap')}>
            {formData.message || ''}
          </p>
        </div>
      )}
      {cs.sections.date && (
        <p className="text-[10px] text-center opacity-30 tracking-wider">
          {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
        </p>
      )}
    </div>
  );
}

function ElegantClassicLayout({ formData, photos, cs, sizes }: LayoutInnerProps) {
  const hFont = getHeadlineFontClass(cs.headlineFont);
  const bFont = getBodyFontClass(cs.bodyFont);
  const align = cs.textAlignment === 'left' ? 'text-left' : cs.textAlignment === 'mixed' ? 'text-left' : 'text-center';

  return (
    <div className={cn('p-6 md:p-10 space-y-6', align)}>
      {cs.sections.subtitle && (
        <p className="text-[10px] uppercase tracking-[0.3em] opacity-50">A message for you</p>
      )}
      {cs.sections.title && (
        <h2 className={cn(hFont, sizes.heading, cs.titleBold && 'font-bold')}>
          For {formData.recipientName || 'Someone Special'}
        </h2>
      )}
      <div className={cn('flex items-center gap-3', align === 'text-center' ? 'justify-center' : '')}>
        <div className="w-10 h-px bg-current opacity-20" />
        <div className="w-1.5 h-1.5 rounded-full bg-current opacity-30" />
        <div className="w-10 h-px bg-current opacity-20" />
      </div>
      {photos.length > 0 && (
        <PhotoBlock urls={photos} shape={cs.photoShape} layout={cs.photoLayout} className="my-4" />
      )}
      {cs.sections.fromLine && (
        <p className="text-xs opacity-50 italic">From your loving heart</p>
      )}
      {cs.sections.body && (
        <p className={cn(bFont, sizes.body, 'leading-relaxed whitespace-pre-wrap max-w-lg', align === 'text-center' && 'mx-auto')}>
          {formData.message || ''}
        </p>
      )}
      {cs.sections.signOff && (
        <div className="pt-4">
          <div className="w-16 h-px bg-current opacity-20 mb-3" style={{ margin: align === 'text-center' ? '0 auto 12px' : undefined }} />
          <p className="text-sm italic opacity-60">With heartfelt sincerity</p>
        </div>
      )}
      <p className="text-[10px] opacity-20 tracking-wider pt-4">
        Say It NOW • While They're Still Here
      </p>
    </div>
  );
}

// ======= INNER PROPS =======
interface LayoutInnerProps {
  formData: AnnouncementFormData;
  photos: string[];
  cs: CustomTemplateSettings;
  sizes: { heading: string; body: string };
}

const LAYOUT_MAP: Record<LayoutFamily, React.ComponentType<LayoutInnerProps>> = {
  magazine_cover: MagazineCoverLayout,
  editorial_story: EditorialStoryLayout,
  love_letter: LoveLetterLayout,
  scrapbook: ScrapbookLayout,
  minimal_modern: MinimalModernLayout,
  polaroid_collage: PolaroidCollageLayout,
  bold_poster: BoldPosterLayout,
  soft_romantic: SoftRomanticLayout,
  memory_board: MemoryBoardLayout,
  elegant_classic: ElegantClassicLayout,
};

export function CustomLayoutRenderer({ formData, allPhotoUrls = [], className }: CustomLayoutRendererProps) {
  const cs = formData.customSettings;
  const photos = allPhotoUrls.length > 0 ? allPhotoUrls : formData.photoUrls || [];
  const sizes = useMemo(() => getSmartFontSize((formData.message || '').length), [formData.message]);

  const bgStyle = useMemo(() => {
    return getBackgroundStyleCSS(cs.backgroundStyle, cs.backgroundColor, cs.backgroundColor);
  }, [cs.backgroundStyle, cs.backgroundColor]);

  const textStyle: React.CSSProperties = useMemo(() => ({ color: cs.fontColor }), [cs.fontColor]);

  const isDark = cs.backgroundStyle === 'dark_dramatic' || cs.backgroundColor === '#1a1a2e' || cs.backgroundColor === '#0f172a' || cs.backgroundColor === '#18181b';

  const LayoutComponent = LAYOUT_MAP[cs.layoutFamily] || ElegantClassicLayout;

  return (
    <div
      className={cn('relative overflow-hidden', isDark && 'text-white', className)}
      style={{ ...bgStyle, ...textStyle }}
    >
      {/* Decoration overlays */}
      <DecorationOverlays decorations={cs.decorations} fontColor={cs.fontColor} />

      {/* Floating elements (existing feature) */}
      {cs.floatingElements.some(e => e.enabled) && (
        <FloatingParticles elements={cs.floatingElements} />
      )}

      {/* Layout content */}
      <div className="relative z-10">
        <LayoutComponent formData={formData} photos={photos} cs={cs} sizes={sizes} />
      </div>
    </div>
  );
}

// Re-use existing floating particles
function FloatingParticles({ elements }: { elements: { type: string; color: string; enabled: boolean }[] }) {
  const active = elements.filter(e => e.enabled);
  if (!active.length) return null;
  const emojiMap: Record<string, string> = { hearts: '♥', suns: '☀', stars: '★', smileys: '☺' };

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none z-[6]">
      {active.flatMap((el, elIdx) =>
        Array.from({ length: 6 }).map((_, i) => (
          <span
            key={`${el.type}-${i}`}
            className="absolute animate-float-up text-lg opacity-40"
            style={{
              color: el.color,
              left: `${5 + (i * 15) + (elIdx * 5)}%`,
              top: `${10 + (i * 13) % 80}%`,
              animationDelay: `${i * 1.0 + elIdx * 0.4}s`,
              animationDuration: `${4 + i * 0.6}s`,
              fontSize: `${14 + (i % 3) * 4}px`,
            }}
          >
            {emojiMap[el.type]}
          </span>
        ))
      )}
    </div>
  );
}
